import { store } from "../store";
import {Vec2, vec2,distance, tovec2} from './core.js'
import { Body, Bodies, Constraint, Composite } from "matter-js";
import {createNoise3D} from 'simplex-noise'

export default class SoftBody {
  constructor(cell) {
    this.bodies = [];
    this.springs = [];
    this.cell = cell;
    this.createBodies(this.cell);
    this.createSprings(this.cell);
    this.n = createNoise3D()
  }
  addToMatter(world){
    Composite.add(world, this.bodies);
    Composite.add(world, this.springs);
  }
  createBodies(cell) {
    let frictionAir = 0.05
    let frictionStatic = 0.5
    let restitution = 0.2
    this.bodies.push(
      Bodies.circle(cell.vertices[0].x, cell.vertices[0].y, cell.radius * 0.45, {
        restitution,
        friction: 1,
        frictionAir,
        frictionStatic
      })
    );
    this.bodies[0].position.sub = Vec2.prototype.sub

    for (let layer = 0; layer < cell.numLayers; layer++) {
      for (let i = 0; i < cell.resolution; i++) {
        let ci = layer * cell.resolution + i + 1;
        let ni = layer * cell.resolution + ((i + 1) % cell.resolution) + 1;
        let pi = layer * cell.resolution + ((i - 1) % cell.resolution) + 1;
        let d1 = distance(cell.vertices[ni], cell.vertices[ci]);
        let d2 = distance(cell.vertices[pi], cell.vertices[ci]);
        let r = d1 > d2 ? d2 : d1; //smaller of the distances
        let b = Bodies.circle(
          cell.vertices[ci].x,
          cell.vertices[ci].y,
          r * 0.48,
          {
            restitution,
            friction: 1,
            frictionAir,
            frictionStatic
          }
        );
        b.position.sub = Vec2.prototype.sub
        this.bodies.push(b);
      }
    }
  }
  move() {
    let c = store.canvasCenter
    this.bodies.forEach(b=>{
        let dN = this.cell.radius * 0.000_05
        let a = store.p5.noise(
            b.position.x*dN + this.cell.id,
            b.position.y*dN + this.cell.id,
            store.p5.frameCount*0.001+this.cell.id) * 30
        let force = this.cell.radius * 0.000_000_0065
        let toCenter = c.sub(b.position).normalized().mul(tovec2(0.000_000_035))
        let accel = vec2(Math.cos(a)*force,Math.sin(a)*force)
        accel = accel.add(toCenter)
        Body.applyForce(b,b.position,{
            x: accel.x,
            y: accel.y 
        })
    })
  }
  stretch(){
    let t = store.p5.frameCount
    t
    for(let i = 1; i < this.bodies.length; i++){
      let dir = this.bodies[i].position.sub(this.bodies[0].position)
      let len = dir.length()
      let z = this.cell.radius * 0.0006
      let r = this.n(this.bodies[i].position.x*z,this.bodies[i].position.y*z,t*0.005+this.cell.id)  
      let mag = (Math.pow(this.cell.radius,2) + len) * 0.000_000_000_01
      dir = dir.mul(tovec2(r)).mul(tovec2(mag))
      Body.applyForce(this.bodies[i],this.bodies[i].position,{
        x:dir.x,
        y: dir.y
      })

    }

  }
  
  getBorderNodes() {
    let nodes = [];
    for (let i = 0; i < this.cell.resolution; i++) {
      let index = this.cell.resolution * (this.cell.numLayers - 1) + i + 1;
      nodes.push(this.bodies[index]);
    }
    return nodes;
  }
  getEdges() {
    let contour = [];
    for (let i = 0; i < this.cell.resolution; i++) {
      let id = this.cell.resolution * (this.cell.numLayers - 1) + i + 1;
      contour.push([this.bodies[id].position.x, this.bodies[id].position.y]);
    }
    return contour;
  }
  getRadius() {
    let maxDist = 0;
    for (let i = 0; i < this.cell.resolution; i++) {
      let index = i + this.cell.resolution * (this.cell.numLayers - 1) + 1;
      let dist = distance(this.bodies[0].position, this.bodies[index].position);
      if (dist > maxDist) {
        maxDist = dist;
      }
    }
    return maxDist;
  }
  createSprings(cell) {
    for (let layer = 0; layer < cell.numLayers; layer++) {
      for (let i = 0; i < cell.resolution; i++) {
        if (layer == 0) {
          this.springs.push(
            Constraint.create({
              //layer 1 to center
              bodyA: this.bodies[0],
              bodyB: this.bodies[i + 1],
              stiffness: 0.01,
              damping: 0.001,
            })
          );
        } else {
          let damp = 0.001;
          let p11 = cell.resolution * (layer - 1) + i + 1;
          let p12 = cell.resolution * layer + ((i + 1) % cell.resolution) + 1;
          let p21 =
            cell.resolution * (layer - 1) + ((i + 1) % cell.resolution) + 1;
          let p22 = cell.resolution * layer + i + 1;
          this.springs.push(
            Constraint.create({
              //layer to center
              bodyA: this.bodies[0],
              bodyB: this.bodies[p22],
              stiffness: 0.001,
              damping: damp,
            })
          );
          this.springs.push(
            Constraint.create({
              //cross beam
              bodyA: this.bodies[p11],
              bodyB: this.bodies[p12],
              stiffness: 0.05,
              damping: 0.8,
            })
          );
          this.springs.push(
            Constraint.create({
              //cross beam
              bodyA: this.bodies[p21],
              bodyB: this.bodies[p22],
              stiffness: 0.05,
              damping: 0.8,
            })
          );
          this.springs.push(
            Constraint.create({
              //lateral
              bodyA: this.bodies[p12],
              bodyB: this.bodies[p21],
              stiffness: 0.05,
              damping: 0.8,
            })
          );
        }
        let ci = cell.resolution * layer + i + 1;
        let ni = cell.resolution * layer + ((i + 1) % cell.resolution) + 1;
        let nni = cell.resolution * layer + ((i + 5) % cell.resolution) + 1;
        let nnni = cell.resolution * layer + ((i + 10) % cell.resolution) + 1;
        this.springs.push(
          Constraint.create({
            bodyA: this.bodies[ci],
            bodyB: this.bodies[ni],
            stiffness: 0.05,
            damping: 0.8,
          })
        );
        this.springs.push(
          Constraint.create({
            bodyA: this.bodies[ci],
            bodyB: this.bodies[nni],
            stiffness: 0.05,
            damping: 0.8,
          })
        );
        this.springs.push(
          Constraint.create({
            bodyA: this.bodies[ci],
            bodyB: this.bodies[nnni],
            stiffness: 0.3,
            damping: 0.6,
          })
        );
      }
    }
  }
}
